import React, { useState, useCallback, useEffect } from 'react'
import { Formik, Form, Field } from 'formik'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import LayoutWithAccountCard from '../../../../layout/Layout/layout-account-card'
import NumberFormat from 'react-number-format'
import {
  BENEFICIARY_TRANSFER_KEYS,
  SIDENAV_KEYS,
  DIALOG_KEYS,
  ADD_BENEFICIARY,
} from '../../../../utils/translation-constants'
import { NeoOTP, NeoSuccess } from '../../../../components/FormComponents'
import NeoButton from '../../../../components/NeoButton/NeoButton'
import NeoInput from '../../../../components/NeoInput/NeoInput'
import InputError from '../../../../components/InputError/InputError'
import Loading from 'custom/assets/loaders/Loading'
import { AddThirdPartyBeneficiarySchema } from '../../../../schemas/beneficiary-transfer'
import BeneficiaryTransferService from '../../../../services/beneficiary-transfers.service'
import TermsAndCondition from '../../../../components/TermsAndConditions/TermsAndCondition'
import config from 'custom/config/environment'

import validationConfig from 'custom/config/validation.config.json'
const domesticBeneConfig = validationConfig['addBeneficiary']['domestic']

export default function AddDomesticBeneficiary() {
  const history = useHistory()
  const [stage, setStage] = useState(0)
  const [otpEnabled, setOtpEnabled] = useState(false)
  const [formValues, setFormValues] = useState({
    benName: '',
    nickName: '',
    accountNumber: '',
    confirmAccountNumber: '',
    bankName: '',
    branchName: '',
    transferAmount: undefined,
    maxTransferAmount: '',
    beneMobileNo: '',
    beneEMail: '',
  })
  const [banks, setBanks] = useState([])
  const [countries, setCountries] = useState([])
  const [branches, setBranches] = useState([])
  const [requestBanks, setRequestBanks] = useState([])
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [otp, setOtp] = useState('')
  const { t } = useTranslation()
  const [selectedBank, setSelectedBank] = useState('')
  const [verifiedBeneDetails, setVerifiedBeneDetails] = useState({})
  const [selectedBranch, setSelectedBranch] = useState({})

  useEffect(() => {
    fetchBanks()
    fetchCountries()
  }, [])

  useEffect(() => {
    const branchList = [...new Set(requestBanks.filter((v) => v.bankName === selectedBank))]
    setBranches(branchList)
  }, [selectedBank, requestBanks])

  const fetchBanks = useCallback(async () => {
    const response = await BeneficiaryTransferService.getAllBanks()
    setRequestBanks(response)
    const bankList = [...new Set(response.map((v) => v.bankName))]
    setBanks(bankList)
  }, [])

  const fetchCountries = useCallback(async () => {
    const response = await BeneficiaryTransferService.getCountries()
    setCountries(response)
  }, [])

  function renderSwitch() {
    switch (stage) {
      case 1:
        return renderForm()
      case 2:
        return renderConfirmation()
      case 3:
        return <NeoOTP otp={otp} setOTP={setOtp} onSubmit={onConfirm} loading={loading} />
      case 4:
        return <NeoSuccess message={message} linkPath='/beneficiary-transfers' />
      default:
        return renderForm()
    }
  }

  const onVerify = useCallback(
    async (values) => {
      try {
        if (values.transferAmount < config.MIN_TRANSFER_LIMIT) {
          return
        }
        // setFormValues(values)
        setLoading(true)
        const bankInformation = requestBanks.filter((x) => x.bankName === values.bankName)[0]
        // let bankInformation = {}
        // if (config.BENE_BRANCH && config.CLIENT_NAME !== 'NBS') {
        //   bankInformation = requestBanks.filter(
        //     (ele) => ele.address === values.branchName,
        //   )[0]
        // } else {
        //   bankInformation = requestBanks.filter((x) => x.bankName === values.bankName)[0]
        // }
        setFormValues({ ...values, ...bankInformation })
        const response = await BeneficiaryTransferService.verifyAddOtherBeneficiary({
          addrline: '',
          bankCode: bankInformation.bankCode,
          bankName: bankInformation.bankName,
          srcAccount: values.accountNumber,
          beneBrn: '',
          beneCurrency: config.LOCAL_CURRENCY_CODE,
          beneName: values.benName,
          bicCode: bankInformation.bicCode,
          city: '',
          beneType: 'THIRD_PARTY',
          maxTransferLimit: values.maxTransferAmount,
          shortName: values.nickName,
          sortCode: bankInformation.sortCode,
          favorites: 'N',
          terms: 'Y',
          beneMobileNo: values.beneMobileNo,
          beneEMail: values.beneEMail,
          module: 'Beneficary',
          modeOfTransfer: '',
          beneCountry: '',
          bankCountry: '',
        })

        if (response.status === 200) {
          setOtpEnabled(response.data['otpEnable'])
          setLoading(false)
          setStage(2)
        }
      } catch (err) {
        setLoading(false)
      }
    },
    [setFormValues, setStage, requestBanks],
  )

  const onConfirm = useCallback(async () => {
    setLoading(true)
    try {
      const response = await BeneficiaryTransferService.addOtherBeneficiary({
        addrline: '',
        bankCode: formValues.bankCode,
        bankName: formValues.bankName,
        srcAccount: formValues.accountNumber,
        beneBrn: '',
        beneCurrency: config.LOCAL_CURRENCY_CODE,
        beneName: formValues.benName,
        bicCode: formValues.bicCode,
        city: '',
        beneType: 'THIRD_PARTY',
        maxTransferLimit: formValues.maxTransferAmount,
        shortName: formValues.nickName,
        sortCode: formValues.sortCode,
        favorites: 'N',
        terms: 'Y',
        beneMobileNo: formValues.beneMobileNo,
        beneEMail: formValues.beneEMail,
        module: 'Beneficary',
        modeOfTransfer: '',
        beneCountry: '',
        bankCountry: '',
        otp: otp || '0000',
      })
      if (response.status === 200) {
        setLoading(false)
        setMessage(response.data.message)
        setStage(4)
      }
    } catch (err) {
      setLoading(false)
      setOtp('')
    }
  }, [formValues, requestBanks, otp])

  function renderForm() {
    return (
      <div>
        <div className='heading-style'>
          {t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.ADDDOMESTIC)}
        </div>
        <Formik
          initialValues={formValues}
          enableReinitialize
          validationSchema={AddThirdPartyBeneficiarySchema}
          onSubmit={onVerify}>
          {({ isSubmitting, errors, touched, values, setFieldValue, handleChange }) => (
            <Form>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='1'
                    showAsterisk={domesticBeneConfig['isBeneNameRequired']}
                    value={values.benName}
                    text={t(ADD_BENEFICIARY.DOMESTIC.BENEFICIARY_NAME)}
                    name='benName'
                    onChange={handleChange}
                    minLength={2}
                    maxLength={32}
                    onKeyPress={(event) => {
                      if (
                        !(event.charCode >= 65 && event.charCode <= 90) &&
                        !(event.charCode >= 97 && event.charCode <= 122) &&
                        event.charCode !== 32
                      ) {
                        event.preventDefault()
                      }
                    }}
                  />
                  <InputError text={errors.benName} touched={touched.benName} />
                </div>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='2'
                    showAsterisk={domesticBeneConfig['isNickNameRequired']}
                    text={t(ADD_BENEFICIARY.DOMESTIC.NICK_NAME)}
                    name='nickName'
                    value={values.nickName}
                    onChange={handleChange}
                    minLength={2}
                    maxLength={20}
                    onKeyPress={(event) => {
                      if (
                        !(event.charCode >= 65 && event.charCode <= 90) &&
                        !(event.charCode >= 97 && event.charCode <= 122) &&
                        event.charCode !== 32
                      ) {
                        event.preventDefault()
                      }
                    }}
                  />
                  <InputError text={errors.nickName} touched={touched.nickName} />
                </div>
              </div>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='3'
                    showAsterisk={domesticBeneConfig['isAccountNoRequired']}
                    text={t(ADD_BENEFICIARY.DOMESTIC.ACCOUNT_NUMBER)}
                    name='accountNumber'
                    // type='number'
                    // minLength={5}
                    // maxLength={20}
                    mode='password'
                    onChange={(value) => {
                      setFieldValue('accountNumber', value?.value)
                    }}
                    maxLength={config.DOM_ACC_NO_LENGTH}
                  />
                  <InputError text={errors.accountNumber} touched={touched.accountNumber} />
                </div>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='4'
                    showAsterisk={domesticBeneConfig['isConfirmAccountNoRequired']}
                    text={t(ADD_BENEFICIARY.DOMESTIC.CONFIRM_ACCOUNT_NUMBER)}
                    //type='number'
                    mode='onlyNumber'
                    onChange={(value) => {
                      setFieldValue('confirmAccountNumber', value?.value)
                    }}
                    name='confirmAccountNumber'
                    maxLength={config.DOM_ACC_NO_LENGTH}
                  />
                  <InputError
                    text={errors.confirmAccountNumber}
                    touched={touched.confirmAccountNumber}
                  />
                </div>
              </div>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='5'
                    showAsterisk={domesticBeneConfig['isBeneBankRequired']}
                    text={t(ADD_BENEFICIARY.DOMESTIC.BENEFICIARY_BANK)}
                    name='bankName'
                    mode='select'
                    onChange={(text) => {
                      handleChange(text)
                      setSelectedBank(text.target.value)
                    }}
                    list={banks}
                  />
                  {/* <NeoInput
                    mode='customSelect'
                    text={t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.BANK)}
                    name='bankName'
                    onChange={(e) => {
                      // handleChange(e)
                      setFieldValue('bankName', e.value)
                      setSelectedBank(e.value)
                    }}
                    options={banks.map((t) => ({
                      value: t,
                      label: t,
                    }))}
                  /> */}
                  <InputError text={errors.bankName} touched={touched.bankName} />
                </div>
                {/*  As client raised this we have hide this only for Domsatic NF2-I382   */}
                {/* {config.BENE_BRANCH && (
                  <div className='form-group col-12'>
                    {config.CLIENT_NAME !== 'NBS' ?
                      <NeoInput
                        tabIndex='6'
                        showAsterisk={config.BENE_BRANCH}
                        text={t(ADD_BENEFICIARY.DOMESTIC.BENEFICIARY_BRANCH)}
                        name='branchName'
                        mode='select'
                        onChange={(text) => {
                          handleChange(text)
                          setSelectedBranch(text.target.value)
                        }}
                        list={branches}
                        keyValue='branchDesc'
                      /> :
                      <NeoInput
                        tabIndex='6'
                        showAsterisk={config.BENE_BRANCH}
                        text={t(ADD_BENEFICIARY.DOMESTIC.BENEFICIARY_BRANCH)}
                        name='branchName'
                        value={values.branchName}
                        onChange={handleChange}
                      />
                    }
                    <InputError text={errors.branchName} touched={touched.branchName} />
                  </div>
                )} */}
              </div>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='7'
                    showAsterisk={domesticBeneConfig['isTransferLimitRequired']}
                    text={t(ADD_BENEFICIARY.DOMESTIC.TRANSFER_LIMIT)}
                    type='number'
                    mode='amount'
                    value={values.maxTransferAmount}
                    // onChange={(values) => {
                    //   setFieldValue('maxTransferAmount', values.value)
                    // }}
                    onChange={(value) => {
                      setFieldValue('transferAmount', value.floatValue)
                      setFieldValue('maxTransferAmount', value?.value)
                    }}
                    name='transferAmount'
                  />
                  <InputError text={errors.maxTransferAmount} touched={touched.maxTransferAmount} />
                  <InputError
                    text={`${t(BENEFICIARY_TRANSFER_KEYS.TRANSFER_LIMIT_TXT)} ${
                      config.MIN_TRANSFER_LIMIT
                    }.00`}
                    touched={values.transferAmount < config.MIN_TRANSFER_LIMIT}
                  />
                </div>
              </div>

              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='8'
                    showAsterisk={domesticBeneConfig['isEmailRequired']}
                    text={t(ADD_BENEFICIARY.DOMESTIC.BENEFICIARY_EMAIL)}
                    name='beneEMail'
                    value={values.beneEMail}
                    onChange={handleChange}
                    maxLength='30'
                  />
                  <InputError text={errors.beneEMail} touched={touched.beneEMail} />
                </div>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='9'
                    showAsterisk={domesticBeneConfig['isMobileNoRequired']}
                    text={t(ADD_BENEFICIARY.DOMESTIC.BENEFICIARY_MOBILE)}
                    onChange={(value) => {
                      setFieldValue('beneMobileNo', value?.value)
                    }}
                    mode='onlyNumber'
                    name='beneMobileNo'
                    minLength={8}
                    maxLength={15}
                  />
                  <InputError text={errors.beneMobileNo} touched={touched.beneMobileNo} />
                </div>
              </div>
              <div className='mt-3'>
                {loading ? (
                  renderLoading()
                ) : (
                  <NeoButton
                    tabIndex='10'
                    type='submit'
                    text={t(ADD_BENEFICIARY.SUBMIT)}
                    disabled={isSubmitting}
                    mode='newDesign'
                  />
                )}
              </div>
              <div className='back-cancel-btn-container'>
                <button
                  className='back-cancel-btn btn btn-sm new-design-warning-text-color'
                  tabIndex={'11'}
                  onClick={() => history.goBack()}>
                  {t(SIDENAV_KEYS.TOUCH_ID_KEYS.CANCEL)}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    )
  }

  function renderConfirmation() {
    return (
      <div className='d-flex flex-column confirmation'>
        <h3>{t(BENEFICIARY_TRANSFER_KEYS.ADD_BENEFICIARY_BANK_KEYS.CONFIRMDOMESTIC)}</h3>
        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY.DOMESTIC.BENEFICIARY_NAME)}</h6>
          <h6 className='value'>{formValues.benName}</h6>
        </div>
        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY.DOMESTIC.NICK_NAME)}</h6>
          <h6 className='value'>{formValues.nickName}</h6>
        </div>

        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY.DOMESTIC.ACCOUNT_NUMBER)}</h6>
          <h6 className='value'>{formValues.accountNumber}</h6>
        </div>

        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY.DOMESTIC.BENEFICIARY_BANK)}</h6>
          <h6 className='value'>{formValues.bankName}</h6>
        </div>
        {/* {config.BENE_BRANCH && (
          <div>
            <h6 className='label'>{t(ADD_BENEFICIARY.DOMESTIC.BENEFICIARY_BRANCH)}</h6>
            <h6 className='value'>{formValues.branchName}</h6>
          </div>
        )} */}

        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY.DOMESTIC.TRANSFER_LIMIT)}</h6>
          <h6 className='value'>
            <NumberFormat
              value={formValues.maxTransferAmount}
              displayType={'text'}
              thousandSeparator={true}
              prefix={''}
              fixedDecimalScale={true}
              decimalScale={2}
              allowEmptyFormatting
            />
          </h6>
        </div>

        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY.DOMESTIC.BENEFICIARY_EMAIL)}</h6>
          <h6 className='value'>{formValues.beneEMail}</h6>
        </div>

        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY.DOMESTIC.BENEFICIARY_MOBILE)}</h6>
          <h6 className='value'>{formValues.beneMobileNo}</h6>
        </div>

        <div>
          <TermsAndCondition module='BENEFICIARY' />
        </div>

        <div className='mt-3'>
          {loading ? (
            renderLoading()
          ) : (
            <NeoButton
              type='button'
              mode='newDesign'
              text={t(ADD_BENEFICIARY.CONFIRM)}
              onClick={() => (otpEnabled ? setStage(3) : onConfirm())}
            />
          )}
        </div>
        <div className='back-cancel-style' onClick={() => setStage(1)}>
          {' '}
          {t(SIDENAV_KEYS.TOUCH_ID_KEYS.BACK)}
        </div>
      </div>
    )
  }

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='50px' height='50px' />
      </div>
    )
  }

  return (
    <LayoutWithAccountCard breadCumKey='BENE_DOMESTIC'>
      <div className='new-design-form-style'>{renderSwitch()}</div>
    </LayoutWithAccountCard>
  )
}
